import { useState, useEffect, useRef } from 'react';
import { FiCamera, FiSave, FiInstagram, FiFacebook, FiTwitter } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { updateVendorProfile } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import VendorLayout from '../../components/layout/VendorLayout';
import './VendorSettings.css';

const CATEGORIES = ['Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Beauty', 'Books', 'Toys', 'Food', 'Automotive', 'Other'];

export default function VendorSettings() {
  const { vendor, updateVendor } = useAuth();
  const logoRef = useRef(null);
  const bannerRef = useRef(null);

  const [storeForm, setStoreForm] = useState({
    storeName: '', description: '', category: '',
    instagram: '', facebook: '', twitter: '',
  });
  const [bankForm, setBankForm] = useState({
    accountName: '', accountNumber: '', ifscCode: '', bankName: '', upiId: '',
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [savingStore, setSavingStore] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  useEffect(() => {
    if (vendor) {
      setStoreForm({
        storeName: vendor.storeName || '',
        description: vendor.description || '',
        category: vendor.category || '',
        instagram: vendor.socialLinks?.instagram || '',
        facebook: vendor.socialLinks?.facebook || '',
        twitter: vendor.socialLinks?.twitter || '',
      });
      setBankForm({
        accountName: vendor.bankDetails?.accountName || '',
        accountNumber: vendor.bankDetails?.accountNumber || '',
        ifscCode: vendor.bankDetails?.ifscCode || '',
        bankName: vendor.bankDetails?.bankName || '',
        upiId: vendor.bankDetails?.upiId || '',
      });
      if (vendor.logo) setLogoPreview(vendor.logo);
      if (vendor.banner) setBannerPreview(vendor.banner);
    }
  }, [vendor]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleStoreChange = (e) => {
    const { name, value } = e.target;
    setStoreForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveStore = async (e) => {
    e.preventDefault();
    if (!storeForm.storeName.trim()) return toast.error('Store name is required');
    setSavingStore(true);
    try {
      const fd = new FormData();
      fd.append('storeName', storeForm.storeName.trim());
      fd.append('description', storeForm.description.trim());
      fd.append('category', storeForm.category);
      fd.append('socialLinks[instagram]', storeForm.instagram);
      fd.append('socialLinks[facebook]', storeForm.facebook);
      fd.append('socialLinks[twitter]', storeForm.twitter);
      if (logoFile) fd.append('logo', logoFile);
      if (bannerFile) fd.append('banner', bannerFile);

      const { data } = await updateVendorProfile(fd);
      updateVendor(data.vendor);
      toast.success('Store settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSavingStore(false);
    }
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      const fd = new FormData();
      fd.append('bankDetails[accountName]', bankForm.accountName);
      fd.append('bankDetails[accountNumber]', bankForm.accountNumber);
      fd.append('bankDetails[ifscCode]', bankForm.ifscCode);
      fd.append('bankDetails[bankName]', bankForm.bankName);
      fd.append('bankDetails[upiId]', bankForm.upiId);

      const { data } = await updateVendorProfile(fd);
      updateVendor(data.vendor);
      toast.success('Bank details saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save bank details');
    } finally {
      setSavingBank(false);
    }
  };

  return (
    <VendorLayout title="Store Settings">
      <div className="vsettings-page">
        {/* Banner & Logo */}
        <form className="card vsettings-section" onSubmit={handleSaveStore}>
          <h2 className="vsettings-title">Store Branding</h2>

          {/* Banner */}
          <div className="vs-banner-wrap">
            <div
              className="vs-banner"
              style={bannerPreview ? { backgroundImage: `url(${bannerPreview})` } : {}}
            >
              {!bannerPreview && <span className="vs-banner-placeholder">Store Banner</span>}
              <button type="button" className="vs-banner-btn" onClick={() => bannerRef.current?.click()}>
                <FiCamera size={14} /> Change Banner
              </button>
            </div>
            <input ref={bannerRef} type="file" accept="image/*" hidden onChange={handleBannerChange} />

            {/* Logo overlay */}
            <div className="vs-logo-wrap">
              <div className="vs-logo">
                {logoPreview
                  ? <img src={logoPreview} alt="logo" />
                  : <span>{storeForm.storeName?.[0]?.toUpperCase() || 'S'}</span>}
              </div>
              <button type="button" className="vs-logo-btn" onClick={() => logoRef.current?.click()}>
                <FiCamera size={12} />
              </button>
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={handleLogoChange} />
            </div>
          </div>

          {/* Store info fields */}
          <div className="vsettings-grid">
            <div className="form-group">
              <label className="form-label">Store Name *</label>
              <input name="storeName" className="form-input" value={storeForm.storeName} onChange={handleStoreChange} placeholder="My Awesome Store" />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select name="category" className="form-select" value={storeForm.category} onChange={handleStoreChange}>
                <option value="">— Select —</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Store Description</label>
            <textarea name="description" className="form-textarea" rows={4} value={storeForm.description} onChange={handleStoreChange} placeholder="Tell buyers about your store…" />
          </div>

          {/* Social links */}
          <div className="vsettings-grid">
            <div className="form-group">
              <label className="form-label"><FiInstagram size={13} /> Instagram</label>
              <input name="instagram" className="form-input" value={storeForm.instagram} onChange={handleStoreChange} placeholder="https://instagram.com/yourstore" />
            </div>
            <div className="form-group">
              <label className="form-label"><FiFacebook size={13} /> Facebook</label>
              <input name="facebook" className="form-input" value={storeForm.facebook} onChange={handleStoreChange} placeholder="https://facebook.com/yourstore" />
            </div>
            <div className="form-group">
              <label className="form-label"><FiTwitter size={13} /> Twitter / X</label>
              <input name="twitter" className="form-input" value={storeForm.twitter} onChange={handleStoreChange} placeholder="https://twitter.com/yourstore" />
            </div>
          </div>

          <div className="vsettings-footer">
            <button type="submit" className="btn btn-primary" disabled={savingStore}>
              <FiSave size={14} /> {savingStore ? 'Saving…' : 'Save Store Settings'}
            </button>
          </div>
        </form>

        {/* Bank details */}
        <form className="card vsettings-section" onSubmit={handleSaveBank}>
          <h2 className="vsettings-title">Bank Details <span className="vsettings-subtitle">for payouts</span></h2>
          <p className="vsettings-note">Your bank details are encrypted and used only for processing payouts.</p>

          <div className="vsettings-grid">
            <div className="form-group">
              <label className="form-label">Account Holder Name</label>
              <input name="accountName" className="form-input" value={bankForm.accountName} onChange={handleBankChange} placeholder="As per bank records" />
            </div>
            <div className="form-group">
              <label className="form-label">Bank Name</label>
              <input name="bankName" className="form-input" value={bankForm.bankName} onChange={handleBankChange} placeholder="HBL / Meezan / MCB" />
            </div>
          </div>
          <div className="vsettings-grid">
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input name="accountNumber" className="form-input" type="password" value={bankForm.accountNumber} onChange={handleBankChange} placeholder="••••••••••" autoComplete="off" />
            </div>
            <div className="form-group">
              <label className="form-label">IFSC Code</label>
              <input name="ifscCode" className="form-input" value={bankForm.ifscCode} onChange={handleBankChange} placeholder="SBIN0001234" style={{ textTransform: 'uppercase' }} />
            </div>
          </div>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="form-label">UPI ID <span className="ap-hint-inline">(optional)</span></label>
            <input name="upiId" className="form-input" value={bankForm.upiId} onChange={handleBankChange} placeholder="yourname@upi" />
          </div>

          <div className="vsettings-footer">
            <button type="submit" className="btn btn-primary" disabled={savingBank}>
              <FiSave size={14} /> {savingBank ? 'Saving…' : 'Save Bank Details'}
            </button>
          </div>
        </form>
      </div>
    </VendorLayout>
  );
}
